import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { Label, Input } from "reactstrap";
import { MDBDataTable } from "mdbreact";
import {
  getPlans,
  getSinglePlan,
  getSinglePlanHistory,
  getWalletTransactions,
} from "../../../store/actions";
import { ProfileNavBar } from "../../dashboard/ProfileNavbar";
import JsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../../asset/RFSLogoFullColour.png";

import moment from "moment";
import PaginatedTable from "../../PaginatedTable";

const Statement = () => {
  const date = {
    startDate: "",
    endDate: "",
  };
  const [category, setCategory] = useState("");
  const [planId, setPlanId] = useState();
  // const [show, setShow] = useState(false);
  const [formData, setFormData] = useState(date);
  const [filteredWalletTransactions, setFilteredWalletTransactions] = useState(
    []
  );
  const [filteredPlanTransactions, setFilteredPlanTransactions] = useState([]);
  const dispatch = useDispatch();

  const profile = useSelector((state) => state.user_profile);
  const { users } = profile;

  const { plans, single_plan_history, singlePlan } = useSelector(
    (state) => state.plan
  );
  const { walletTransactions } = useSelector((state) => state.wallet);
  const userPlans = plans?.data.body ? plans?.data.body : [];
  const planHistory = useMemo(
    () => (single_plan_history ? single_plan_history?.content : []),
    [single_plan_history]
  );
  const walletHistory = useMemo(
    () => (walletTransactions ? walletTransactions?.entities : []),
    [walletTransactions]
  );
  const plan = singlePlan?.data.body ? singlePlan?.data.body : {};

  const checkHistoryDisplay = useCallback(() => {
    let history = [];
    if (category === "WALLET") {
      history = walletHistory;
    } else if (category === "PLAN" && planId) {
      history = planHistory;
    }
    return history;
  }, [category, planHistory, planId, walletHistory]);

  const viewableUserPlans = userPlans.filter(
    (data) => data.planStatus !== "PENDING"
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  useEffect(() => {
    if (category === "WALLET") dispatch(getWalletTransactions());
  }, [dispatch, category]);

  useEffect(() => {
    if (planId) {
      dispatch(getSinglePlanHistory(parseInt(planId)));
      dispatch(getSinglePlan(planId));
    }
  }, [dispatch, planId]);

  useEffect(() => {
    dispatch(getWalletTransactions());
  }, [dispatch]);

  useEffect(() => {
    if (category === "PLAN") {
      const filteredPlanHistory = checkHistoryDisplay()?.filter((item) => {
        const startDate = new Date(formData.startDate);
        const endDate = new Date(formData.endDate);

        const itemDate = moment(item.dateOfTransaction, "YYYY-MM-DD").format(
          "YYYY-MM-DD"
        );

        const date = new Date(itemDate);

        return date >= startDate && date <= endDate;
      });
      setFilteredPlanTransactions(filteredPlanHistory);
    }

    if (category === "WALLET") {
      const filteredWalletTrans = checkHistoryDisplay()?.filter((item) => {
        const startDate = new Date(formData.startDate);
        const endDate = new Date(formData.endDate);

        const itemDate = moment(item.transactionDate, "DD-MM-YYYY").format(
          "YYYY-MM-DD"
        );

        const date = new Date(itemDate);

        return date >= startDate && date <= endDate;
      });
      setFilteredWalletTransactions(filteredWalletTrans);
    }
  }, [formData, category, checkHistoryDisplay]);

  useEffect(() => {
    dispatch(getPlans());
  }, [dispatch]);

  const month = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const plan_history_list =
    filteredPlanTransactions.length > 0 ||
    (formData.startDate !== "" && formData.endDate !== "")
      ? filteredPlanTransactions.map((item) => ({
          id: `${item?.transactionId}`,
          date: `${moment(item.dateOfTransaction, "YYYY-MM-DD").format(
            "DD-MM-YYYY"
          )}`,
          description: `${item?.description}`,
          type: `${item?.type}`,
          amount: `${item?.amount}`,
          balance: `${item?.balance}`,
        }))
      : checkHistoryDisplay()?.map((item) => ({
          id: `${item?.transactionId}`,
          date: `${moment(item.dateOfTransaction, "YYYY-MM-DD").format(
            "DD-MM-YYYY"
          )}`,
          description: `${item?.description}`,
          type: `${item?.type}`,
          amount: `${item?.amount}`,
          balance: `${item?.balance}`,
        }));

  const planData = {
    columns: [
      {
        label: "ID",
        field: "id",
        width: 150,
      },
      {
        label: "Date",
        field: "date",
        width: 100,
      },
      {
        label: "Description",
        field: "description",
        width: 200,
      },
      {
        label: "Type",
        field: "type",
        width: 100,
      },
      {
        label: `Amount (${plan?.currency?.name})`,
        field: "amount",
        width: 100,
      },
      {
        label: `Balance (${plan?.currency?.name})`,
        field: "balance",
        width: 100,
      },
    ],
    rows: plan_history_list,
  };

  const wallet_history_list =
    filteredWalletTransactions?.length > 0 ||
    (formData.startDate !== "" && formData.endDate !== "")
      ? filteredWalletTransactions?.map((data) => ({
          id: `${data?.transactionId}`,
          date: `${moment(
            data?.transactionDate?.split(" ")[0],
            "DD-MM-YYYY"
          ).format("DD-MM-YYYY")}`,
          description: `${data?.description ? data?.description : ""}`,
          type: `${data?.transactionType}`,
          amount: `${
            data?.transactionType === "CREDIT"
              ? "+ " + data?.debit?.toLocaleString()
              : "- " + data?.debit?.toLocaleString()
          }`,
          balance: `${data?.balanceAfterTransaction?.toLocaleString()}`,
        }))
      : checkHistoryDisplay()?.map((data) => ({
          id: `${data?.transactionId}`,
          date: `${data?.transactionDate?.split(" ")[0]}`,
          description: `${data?.description ? data?.description : ""}`,
          type: `${data?.transactionType}`,
          amount: `${
            data?.transactionType === "CREDIT"
              ? "+ " + data?.debit?.toLocaleString()
              : "- " + data?.debit?.toLocaleString()
          }`,
          balance: `${data?.balanceAfterTransaction?.toLocaleString()}`,
        }));

  const walletData = {
    columns: [
      {
        label: "ID",
        field: "id",
        width: 150,
      },
      {
        label: "Date",
        field: "date",
        width: 100,
      },
      {
        label: "Description",
        field: "description",
        width: 200,
      },
      {
        label: "Type",
        field: "type",
        width: 100,
      },
      {
        label: "Amount (NGN)",
        field: "amount",
        width: 100,
      },
      {
        label: "Balance (NGN)",
        field: "balance",
        width: 100,
      },
    ],
    rows: wallet_history_list,
  };

  const startDate = new Date(formData.startDate);
  const endDate = new Date(formData.endDate);

  function addHeader(doc) {
    // Header content
    doc.setFontSize(12);
    doc.setTextColor(40);
    doc.setFontStyle("normal");
    doc.text("Your Header Content", 20, 15);
  }

  function addFooter(doc) {
    // Footer content
    var pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(12);
    doc.setTextColor(40);
    doc.setFontStyle("normal");
    doc.text(
      "Page " + doc.pageNumber + " of " + pageCount,
      20,
      doc.internal.pageSize.height - 20
    );
  }

  const generatePDF = () => {
    const transaction = new JsPDF({
      orientation: "landscape",
      unit: "pt",
      size: "A4",
    });
    const period =
      formData?.startDate === "" || formData?.endDate === ""
        ? "NA"
        : `${moment(startDate).format("DD-MMM-YYYY")} to ${moment(
            endDate
          ).format("DD-MMM-YYYY")}`;

    const currentDay = moment(new Date()).format("DD-MMM-YYYY");

    const selectedPlan = viewableUserPlans?.find(
      (pln) => pln?.id === parseInt(planId)
    );
    const header = "Rosabon Financial Services Customer Statement";
    const pageWidth = transaction.internal.pageSize.getWidth();

    
    transaction.setFont("Helvetica", "");
    transaction.setFontSize(12);
    const userName =
      users && users.role === "COMPANY"
        ? users.company.name
        : users && users.role === "INDIVIDUAL_USER"
        ? `${users.individualUser.firstName ?? ""} ${
            users.individualUser.middleName ?? ""
          } ${users.individualUser.lastName ?? ""}`
        : "";
    transaction.text(`Name: ${userName}`, 40, 100);
    transaction.text(
      `Account Name: ${
        category === "WALLET" ? category : selectedPlan?.planName
      }`,
      290,
      100
    );
    transaction.text(
      `Interest Rate: ${
        category === "WALLET"
          ? "NA"
          : parseFloat(selectedPlan?.interestRate)?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
      }`,
      540,
      100
    );
    transaction.text(`Period: ${period}`, 40, 130);
    transaction.text(
      `Tenor Ending: ${
        category === "WALLET"
          ? "NA"
          : moment(selectedPlan?.endDate).format("DD-MMM-YYYY")
      }`,
      290,
      130
    );
    transaction.text(`Print Date: ${currentDay}`, 540, 130);

    const footerY = transaction.internal.pageSize.getHeight() - 70;

    // transaction.addPage()
    // addHeader(transaction);
    // addFooter(transaction);

    if (category === "WALLET") {
      // autoTable(transaction, { html: '#wallet-table' })
      const walletLen = Math.ceil(walletData?.rows?.length / 12);
      Array.from({ length: walletLen })?.forEach((wallData, index) => {
        transaction.addImage(logo, "PNG", 40, 30, 80, 40);
        transaction.setFontSize(16);
        transaction.setFont("Helvetica", "Bold");
        transaction.text(header, pageWidth / 2, 60, "center");
        autoTable(transaction, {
          startY: index===0?150:90,
          head: [walletData?.columns?.map((item) => item?.label)],
          body: [
            ...walletData?.rows
              ?.slice(index * 12, (index + 1) * 12)
              ?.map((item) => Object.values(item)),
          ],
        });
        transaction.setFont("Helvetica", "");
        transaction.setFontSize(12);
        transaction.text(
          `
        Rosabon Financial Services
          If you have any questions or need assistance along the way, kindly reach out to our dedicated support team by sending a mail to
          clientservices@rosabon-finance.com or call us  on 0700ROSABON (07007672266)
        `,
          pageWidth / 2,
          footerY,
          "center"
        );
        if(index < walletLen-1) {
          transaction.addPage()
        }
      });
    }

    if (category === "PLAN" && planId) {
      const planLen = Math.ceil(planData?.rows?.length / 12);
      Array.from({ length: planLen })?.forEach((wallData, index) => {
        transaction.addImage(logo, "PNG", 40, 30, 80, 40);
        transaction.setFontSize(16);
        transaction.setFont("Helvetica", "Bold");
        transaction.text(header, pageWidth / 2, 60, "center");
        autoTable(transaction, {
          startY: index===0?150:90,
          head: [planData?.columns?.map((item) => item?.label)],
          body: [
            ...planData?.rows
              ?.slice(index * 12, (index + 1) * 12)
              ?.map((item) => Object.values(item)),
          ],
        });
        transaction.setFont("Helvetica", "");
        transaction.setFontSize(12);
        transaction.text(
          `
        Rosabon Financial Services
          If you have any questions or need assistance along the way, kindly reach out to our dedicated support team by sending a mail to
          clientservices@rosabon-finance.com or call us  on 0700ROSABON (07007672266)
        `,
          pageWidth / 2,
          footerY,
          "center"
        );
        if(index < planLen-1) {
          transaction.addPage()
        }
      });
      // autoTable(transaction, {
      //   startY: 180,
      //   head: [planData?.columns?.map((item) => item?.label)],
      //   body: [...planData?.rows?.map((item) => Object.values(item))],
      // });
    }
    transaction.setFontSize(10);
    transaction.save("transaction.pdf");
  };

  return (
    <div>
      <ProfileNavBar>
        <NavTitle>
          <span className="fw-bold">Statement</span>
        </NavTitle>
      </ProfileNavBar>
      <HistoryTableWarapper>
        <h4>Choose a time frame for your statement</h4>
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-6">
              <div className="row">
                <div className="col-sm-4 col-md-6">
                  <div className=" ">
                    <div className="mb-4">
                      <label>Category</label>
                      <div className="input-group mb-4 mt-1">
                        <select
                          className="form-select form-select-md mb-3 select-field"
                          aria-label=".form-select-md"
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          name="category"
                        >
                          <option value="">Choose Category</option>
                          <option value="WALLET">Wallet</option>
                          <option value="PLAN">Plan</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                {category === "PLAN" && (
                  <div className="col-sm-4 col-md-6">
                    <div className=" ">
                      <div className="mb-4">
                        <label>Plan</label>
                        <div className="input-group mb-4 mt-1">
                          <select
                            className="form-select form-select-md mb-3 select-field"
                            aria-label=".form-select-md"
                            onChange={(e) => setPlanId(e.target.value)}
                            name="Plan"
                            value={planId}
                          >
                            <option value="">Select Plan</option>
                            {viewableUserPlans?.map((data) => (
                              <option key={data.id} value={data.id}>
                                {data.planName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="row">
                <div className="col-sm-4 col-md-6">
                  <div className=" ">
                    <div className="mb-4">
                      <Label>Start Date</Label>
                      <Input
                        className="form-control"
                        placeholder="Start date"
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-sm-4 col-md-6">
                  <div className=" ">
                    <div className="mb-4">
                      <Label>End Date</Label>
                      <Input
                        className="form-control"
                        placeholder="End date"
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h3 className="mb-5">
          {formData.startDate &&
            formData.endDate &&
            `${month[startDate.getMonth()]} ${startDate.getDate()} - ${
              month[endDate.getMonth()]
            } ${endDate.getDate()}`}
        </h3>

        {category === "WALLET" ? (
          <div>
            <div>
              {/* <MDBDataTable
                responsive
                striped
                data={walletData}
                searching={false}
                id="wallet-table"
              /> */}
              <PaginatedTable
                rows={walletData?.rows}
                columns={walletData?.columns}
              />
            </div>
          </div>
        ) : planId ? (
          <div>
            <div>
              {/* <MDBDataTable
                responsive
                striped
                data={planData}
                searching={false}
                id="plan-table"
              /> */}
              <PaginatedTable
                rows={planData?.rows}
                columns={planData?.columns}
              />
            </div>
          </div>
        ) : (
          <></>
        )}

        {category === "WALLET" ? (
          <div className="row">
            <div className="d-flex justify-content-center my-5">
              <button
                className="btn-view"
                onClick={generatePDF}
                disabled={walletData.rows.length === 0}
              >
                Download Pdf
              </button>
            </div>
          </div>
        ) : category === "PLAN" ? (
          <div className="row">
            <div className="d-flex justify-content-center my-5">
              <button
                className="btn-view"
                onClick={generatePDF}
                disabled={planData.rows.length === 0}
              >
                Download Pdf
              </button>
            </div>
          </div>
        ) : null}
      </HistoryTableWarapper>
    </div>
  );
};

export default Statement;

const NavTitle = styled.div`
  display: flex;
  flex-direction: column;
  h2,
  span {
    text-align: left;
  }
  @media (max-width: 500px) {
    h2,
    span {
      display: none;
    }
  }
`;

const HistoryTableWarapper = styled.div`
  padding: 30px;
  tr {
    font-family: "Montserrat";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    letter-spacing: -0.01em;
    color: #242424;
    cursor: pointer;
  }
  th {
    font-family: "Montserrat";
    font-style: normal;
    font-weight: 400;
    font-size: 15px;
    line-height: 20px;
    letter-spacing: -0.03em;
    color: #000000;
  }
  th,
  td {
    padding: 20px;
  }
  input {
    background: #fbf8f8;
    border-radius: 5px;
  }
  h3 {
    font-family: "Montserrat";
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: -0.01em;
    color: #000000;
    padding-top: 50px;
  }
  .grey-text {
    color: #bdbdbd;
    cursor: pointer;
  }
  h4 {
    font-family: "Montserrat";
    font-style: normal;
    font-weight: 600;
    font-size: 17px;
    line-height: 150%;
    letter-spacing: -0.15px;
    color: #242424;
    padding-top: 30px;
    padding-bottom: 20px;
  }

  .btn-view {
    width: 328px;
    height: 54px;
    background: #F2F2F2;
    border-radius: 10px;
    font-weight: 600;
    font-size: 18px;
    line-height: 22px
    color: #111E6C;
  }
`;
